import React, { useCallback, useRef, useState } from "react"

/**
 * Ponto normalizado: x e y em [0, 1] relativos ao quadro completo da câmera.
 * Guardamos normalizado (e não em pixels) de propósito: o mesmo polígono
 * funciona independente da resolução do NVR (ex.: 3200x1800 vs 2560x1440),
 * que é o que a IA recebe no backend para recortar a região de análise.
 */
export interface RoiPoint {
  x: number
  y: number
}

/** Polígono desenhado apenas para referência, sem interação. */
export interface RoiOverlay {
  points: RoiPoint[]
  color: string
  label?: string
}

interface RoiEditorProps {
  points: RoiPoint[]
  onChange: (points: RoiPoint[]) => void
  /** Razão de aspecto (largura/altura) do quadro, ex.: 16/9. */
  aspectRatio?: number
  /** Mídia a ser desenhada por baixo (vídeo ou imagem). */
  children?: React.ReactNode
  disabled?: boolean
  color?: string
  maxPoints?: number
  /**
   * Outras regiões do mesmo quadro, desenhadas em segundo plano.
   *
   * Existem para o usuário ver onde já marcou água e comida enquanto desenha
   * a baia (e vice-versa): sem isso ele desenharia às cegas sobre regiões que
   * não enxerga. Não recebem evento nenhum — os cliques atravessam para a
   * camada ativa.
   */
  overlays?: RoiOverlay[]
}

/**
 * Máximo de pontos do polígono. Limitado pela coluna `cameraRoi` (520 chars).
 * Pior caso por ponto = 23 chars (`{"x":0.1981,"y":0.9921}`), total 24N+1.
 * 21 pontos = 505 chars (cabe); 22 = 529 (estoura). Por isso 21 é o teto.
 */
export const MAX_ROI_POINTS = 21

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

/**
 * Editor de polígono de região de interesse sobreposto a uma mídia.
 *
 * Interações:
 *  - clique numa área vazia: adiciona um vértice;
 *  - arrastar um vértice: reposiciona;
 *  - botão direito (ou duplo clique) num vértice: remove.
 *
 * O componente é controlado: todo o estado do polígono vive em [points].
 */
const RoiEditor: React.FC<RoiEditorProps> = ({
  points,
  onChange,
  aspectRatio = 16 / 9,
  children,
  disabled = false,
  color = "#22c55e",
  maxPoints = MAX_ROI_POINTS,
  overlays = [],
}) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  // Vértice pressionado que ainda não virou arraste. Só promovemos a arraste
  // (e capturamos o ponteiro) no primeiro movimento — capturar já no
  // pointerdown suprimiria os eventos click/dblclick do navegador.
  const pendingRef = useRef<{ index: number; el: Element; pointerId: number } | null>(null)
  // Evita que o pointerup que encerra um arraste vire um "clique" que adiciona
  // um ponto indesejado.
  const draggedRef = useRef(false)

  const toNormalized = useCallback((clientX: number, clientY: number): RoiPoint => {
    const rect = boxRef.current!.getBoundingClientRect()
    const r4 = (v: number) => Math.round(v * 10000) / 10000
    return {
      x: r4(clamp01((clientX - rect.left) / rect.width)),
      y: r4(clamp01((clientY - rect.top) / rect.height)),
    }
  }, [])

  const handleBackgroundPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return
      if (draggedRef.current) {
        draggedRef.current = false
        return
      }
      if (points.length >= maxPoints) return
      // Só adiciona se o alvo for o fundo (svg/mídia), não um vértice.
      const p = toNormalized(e.clientX, e.clientY)
      onChange([...points, p])
    },
    [disabled, points, onChange, toNormalized, maxPoints],
  )

  const handleVertexPointerDown = useCallback(
    (index: number) => (e: React.PointerEvent) => {
      if (disabled) return
      e.stopPropagation()
      // Não captura aqui (suprimiria click/dblclick); só marca como pendente.
      pendingRef.current = { index, el: e.target as Element, pointerId: e.pointerId }
    },
    [disabled],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Promove o vértice pendente a arraste no primeiro movimento.
      let active = dragIndex
      if (active === null && pendingRef.current) {
        const { index, el, pointerId } = pendingRef.current
        try {
          el.setPointerCapture(pointerId)
        } catch {
          /* alvo pode ter sido recriado; ignora */
        }
        active = index
        setDragIndex(index)
        pendingRef.current = null
      }
      if (active === null) return
      draggedRef.current = true
      const p = toNormalized(e.clientX, e.clientY)
      const next = points.slice()
      next[active] = p
      onChange(next)
    },
    [dragIndex, points, onChange, toNormalized],
  )

  const handlePointerUp = useCallback(() => {
    setDragIndex(null)
    pendingRef.current = null
  }, [])

  const handleVertexRemove = useCallback(
    (index: number) => (e: React.SyntheticEvent) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      onChange(points.filter((_, i) => i !== index))
    },
    [disabled, points, onChange],
  )

  const polygonPoints = points.map((p) => `${p.x * 100},${p.y * 100}`).join(" ")
  const closed = points.length >= 3

  return (
    <div
      ref={boxRef}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: `${aspectRatio}`,
        background: "#000",
        overflow: "hidden",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {children}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          cursor: disabled ? "default" : "crosshair",
        }}
      >
        {/* Regiões de referência, por baixo da ativa. `pointerEvents: none`
            para o clique chegar ao fundo e adicionar ponto na camada ativa. */}
        {overlays.map((camada, ci) => {
          if (camada.points.length < 2) return null
          const pts = camada.points.map((p) => `${p.x * 100},${p.y * 100}`).join(" ")
          const fechado = camada.points.length >= 3
          return (
            <g key={`overlay-${ci}`} style={{ pointerEvents: "none" }} opacity={0.55}>
              {fechado && (
                <polygon points={pts} fill={camada.color} fillOpacity={0.12} stroke="none" />
              )}
              <polyline
                points={
                  fechado
                    ? `${pts} ${camada.points[0].x * 100},${camada.points[0].y * 100}`
                    : pts
                }
                fill="none"
                stroke={camada.color}
                strokeWidth={0.4}
                strokeDasharray="1.5 1.2"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          )
        })}

        {/* Preenchimento da região (só quando há área) */}
        {closed && (
          <polygon
            points={polygonPoints}
            fill={color}
            fillOpacity={0.18}
            stroke="none"
          />
        )}

        {/* Arestas */}
        {points.length >= 2 && (
          <polyline
            points={
              closed ? `${polygonPoints} ${points[0].x * 100},${points[0].y * 100}` : polygonPoints
            }
            fill="none"
            stroke={color}
            strokeWidth={0.5}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* Vértices */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x * 100}
            cy={p.y * 100}
            r={1.4}
            fill={i === 0 ? "#fff" : color}
            stroke={color}
            strokeWidth={0.6}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: disabled ? "default" : "grab" }}
            onPointerDown={handleVertexPointerDown(i)}
            onContextMenu={handleVertexRemove(i)}
            onDoubleClick={handleVertexRemove(i)}
          />
        ))}
      </svg>
    </div>
  )
}

export default RoiEditor
